/**
 * Pure rules of the notification service: how long a notification stays, its progress as a
 * percent, the words a screen reader hears, and how a plain-string message splits into a title and
 * a body. Unit-tested on their own; the store (notifyStore.ts) only calls them.
 */
import type { NotificationKind, NotificationProgress } from "./notify.types";

/** Success and info close after this long; hovering or focusing the stack pauses it. */
export const NOTIFICATION_LIFE_MS = 5000;
/** A paused timer always leaves at least this long once it runs again. */
export const NOTIFICATION_MIN_REMAINING_MS = 1500;
/** Recent activity keeps this many. */
export const ACTIVITY_LIMIT = 30;
/** A progress tick is read out again only after this long, so screen readers aren't flooded. */
export const PROGRESS_ANNOUNCE_GAP_MS = 6000;

export const NOTIFICATION_KINDS: readonly NotificationKind[] = ["success", "info", "warning", "error", "progress"];

const KIND_LIFE: Record<NotificationKind, number> = {
  success: NOTIFICATION_LIFE_MS,
  info: NOTIFICATION_LIFE_MS,
  warning: 0,
  error: 0,
  progress: 0,
};

/** Read before the title by screen readers; info needs no word. */
export const KIND_SCREEN_READER_LABEL: Record<NotificationKind, string> = {
  success: "Success",
  info: "",
  warning: "Warning",
  error: "Error",
  progress: "In progress",
};

export const isNotificationKind = (value: unknown): value is NotificationKind =>
  typeof value === "string" && (NOTIFICATION_KINDS as readonly string[]).includes(value);

/** Milliseconds on screen; 0 means it stays until dismissed. */
export const lifeOf = (n: { kind: NotificationKind; sticky?: boolean; duration?: number }): number => {
  if (n.sticky === true) return 0;
  if (n.duration !== undefined && n.duration > 0) return n.duration;
  return n.sticky === false ? NOTIFICATION_LIFE_MS : KIND_LIFE[n.kind];
};

/** 0–100 for a progress notification with a bar; null for any other kind or no progress. */
export const progressPercent = (kind: NotificationKind, progress: NotificationProgress | null | undefined): number | null => {
  if (kind !== "progress" || progress === null || progress === undefined) return null;
  const share = typeof progress === "number" ? progress : progress.total > 0 ? progress.done / progress.total : 0;
  const safe = Number.isFinite(share) ? share : 0;
  return Math.round(Math.max(0, Math.min(1, safe)) * 100);
};

/** "Error: Run failed. Nothing was sent." — what a live region reads, once per event. */
export const announcementText = (kind: NotificationKind, title: string, body: string): string => {
  const label = KIND_SCREEN_READER_LABEL[kind];
  const sentence = /[.!?…]$/.test(title) ? title : `${title}.`;
  return `${label ? `${label}: ` : ""}${sentence}${body ? ` ${body}` : ""}`;
};

/**
 * A plain-string message as a title and a body: "Draft saved. Resume it any time." becomes
 * "Draft saved" + "Resume it any time.". A single sentence stays whole, as the title.
 */
export const splitMessage = (message: string): { title: string; body: string } => {
  const text = message.trim();
  const match = /^([\s\S]{2,100}?[.!?])\s+(?=[A-Z0-9“"‘'(])([\s\S]+)$/.exec(text);
  const title = (match ? match[1] : text).replace(/([^.])\.$/, "$1");
  return { title, body: match ? match[2].trim() : "" };
};

/** "Just now", "4 min ago", "2 hr ago", then the date ("Oct 13"), from `nowMs`. */
export const relativeTime = (atMs: number, nowMs: number, locale?: string): string => {
  const seconds = (nowMs - atMs) / 1000;
  if (seconds < 45) return "Just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return new Date(atMs).toLocaleDateString(locale, { month: "short", day: "numeric" });
};

/** "Tue, Oct 13, 8:01 AM": the full time, for a tooltip. */
export const absoluteTime = (atMs: number, locale?: string): string =>
  new Date(atMs).toLocaleString(locale, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
